import { useMemo, useState, Fragment } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  ExternalLink,
  Landmark,
  Receipt,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useTransactionHistory,
  type TransactionItem,
  type TransactionKind,
  type CoveredRegistration,
} from '@/api/extensions/useTransactionHistory';
import { formatCurrency } from '@/utils/formatters';
import { ProviderBadge, type Provider } from '@/components/financials/ProviderBadge';
import { buildStripeDashboardUrl } from '@/utils/stripeDashboardUrl';

/**
 * Transaction History tab — every financial event tied to the user's
 * account: Stripe payments + refunds, cash receipts, manual ledger
 * entries (charges / credits / comps), and unpaid pay-later
 * registrations. Distinct from the Funds tab, which is the per-entry
 * view; this tab is the per-event-of-money view, rendered as a ledger
 * table so the columns line up at a single scan.
 */

// Mirror of the local helper in PaymentsTab — short
// "May 13, 2026, 7:45 AM" format used throughout the profile pages.
function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Filter chip values. 'all' is exclusive — picking it wipes the rest;
// re-clicking another chip drops 'all'. Same pattern the Funds tab uses
// so the chip behaviour reads consistent across the Profile page.
type FilterChip =
  | 'all'
  | 'payments'
  | 'refunds'
  | 'manual'
  | 'pending'
  | 'free';

export function matchesFilter(
  row: TransactionItem,
  chips: Set<FilterChip>,
): boolean {
  if (chips.has('all')) return true;
  const labels = new Set<FilterChip>();
  if (row.kind === 'payment' || row.kind === 'failed') labels.add('payments');
  if (row.kind === 'refund') labels.add('refunds');
  if (row.kind === 'adjustment') {
    // Manual refund / credit / comp reduces what the user owes
    // (negative amount) — reachable via Refunds too. Charges are
    // Manual-only. Both signs are reachable via Manual.
    if (row.amount < 0) labels.add('refunds');
    labels.add('manual');
  }
  if (row.kind === 'pending') labels.add('pending');
  // A waived entry is settled at $0, so it belongs to neither Payments (no
  // money moved) nor Pending (nothing owed). Without its own chip it matches
  // no label at all and is reachable only through 'all'.
  if (row.kind === 'free') labels.add('free');
  for (const chip of chips) {
    if (labels.has(chip)) return true;
  }
  return false;
}

// Org filter. '__all__' is the exclusive default — picking it wipes the rest.
// '__none__' is the bucket for rows with no org association at all (a stray
// refund, a failed attempt) so narrowing to a specific org never silently
// hides them — the user can still pick "Other" to see them.
const ALL_ORGS = '__all__';
const NO_ORG = '__none__';

// Every org a row touches: its own org (manual ledger entries) plus the org
// of each covered registration (Stripe payments carry org info there, not at
// the top level, and a bulk payment can span several orgs). Empty → NO_ORG.
function rowOrgNames(row: TransactionItem): string[] {
  const names = new Set<string>();
  if (row.organization_name) names.add(row.organization_name);
  for (const cr of row.covered_registrations) {
    if (cr.organization_name) names.add(cr.organization_name);
  }
  return names.size > 0 ? [...names] : [NO_ORG];
}

function matchesOrg(row: TransactionItem, selected: Set<string>): boolean {
  if (selected.has(ALL_ORGS)) return true;
  return rowOrgNames(row).some((n) => selected.has(n));
}

// Date-range filter — preset windows + a custom span, ported from the retired
// Balance-breakdown modal so the feed can be scoped to a period.
type DatePreset = 'all' | 'today' | '7d' | '30d' | 'month' | 'ytd' | 'custom';

const DATE_PRESETS: Array<{ key: DatePreset; label: string }> = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'month', label: 'This month' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'custom', label: 'Custom' },
];

// Returns the inclusive [startMs, endMs] window (either bound may be null =
// open-ended), or null for "all time" (no filtering).
function computeDateRange(
  preset: DatePreset,
  customStart: string,
  customEnd: string,
): [number | null, number | null] | null {
  if (preset === 'all') return null;
  const now = new Date();
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.getTime();
  };
  switch (preset) {
    case 'today':
      return [startOfDay(now), endOfDay(now)];
    case '7d': {
      const s = new Date(now);
      s.setDate(s.getDate() - 6);
      return [startOfDay(s), endOfDay(now)];
    }
    case '30d': {
      const s = new Date(now);
      s.setDate(s.getDate() - 29);
      return [startOfDay(s), endOfDay(now)];
    }
    case 'month':
      return [
        startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        endOfDay(now),
      ];
    case 'ytd':
      return [startOfDay(new Date(now.getFullYear(), 0, 1)), endOfDay(now)];
    case 'custom':
      return [
        customStart ? startOfDay(new Date(`${customStart}T00:00:00`)) : null,
        customEnd ? endOfDay(new Date(`${customEnd}T00:00:00`)) : null,
      ];
    default:
      return null;
  }
}

function inDateRange(
  row: TransactionItem,
  range: [number | null, number | null] | null,
): boolean {
  if (!range) return true;
  const [s, e] = range;
  const t = new Date(row.occurred_at).getTime();
  if (Number.isNaN(t)) return true;
  if (s != null && t < s) return false;
  if (e != null && t > e) return false;
  return true;
}

function getKindStyle(kind: TransactionKind) {
  switch (kind) {
    case 'payment':
      return {
        badge:
          'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
        amount: 'text-emerald-700 dark:text-emerald-300',
      };
    case 'refund':
      return {
        badge:
          'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
        amount: 'text-violet-700 dark:text-violet-300',
      };
    case 'adjustment':
      return {
        badge:
          'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
        amount: 'text-blue-700 dark:text-blue-300',
      };
    case 'pending':
      return {
        badge:
          'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
        amount: 'text-amber-700 dark:text-amber-300',
      };
    // Waived to $0. Deliberately NOT amber: nothing is owed, so it must not
    // read as an outstanding obligation.
    case 'free':
      return {
        badge:
          'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
        amount: 'text-muted-foreground',
      };
    case 'failed':
      return {
        badge:
          'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
        amount: 'text-rose-700 dark:text-rose-300',
      };
  }
}

function PaymentMethodSummary({
  pm,
}: {
  pm: TransactionItem['payment_method'];
}) {
  if (!pm || (!pm.type && !pm.last4 && !pm.brand && !pm.bank_name)) return null;
  if (pm.type === 'card') {
    const brand = pm.brand
      ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
      : 'Card';
    return (
      <span className="text-xs text-muted-foreground">
        {pm.last4 ? `${brand} •••• ${pm.last4}` : brand}
      </span>
    );
  }
  if (pm.type === 'us_bank_account') {
    const base = pm.bank_name ? `${pm.bank_name} (ACH)` : 'Bank (ACH)';
    return (
      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <Landmark className="h-3 w-3" />
        {pm.last4 ? `${base} •••• ${pm.last4}` : base}
      </span>
    );
  }
  if (pm.type) {
    return (
      <span className="text-xs text-muted-foreground">
        {pm.type.charAt(0).toUpperCase() +
          pm.type.slice(1).replace(/_/g, ' ')}
      </span>
    );
  }
  return null;
}

// Human-readable label for the user-selected method on pending rows
// ('pay_later' → "Pay later", 'cash' → "Cash at event", etc.).
function describeSelectedMethod(method: string | null): string | null {
  if (!method) return null;
  switch (method) {
    case 'pay_later':
      return 'Pay later';
    case 'cash':
      return 'Cash at event';
    case 'stripe':
      return 'Card or bank';
    case 'bank':
      return 'Bank (ACH)';
    case 'paypal':
      return 'PayPal';
    default:
      return method.charAt(0).toUpperCase() + method.slice(1).replace(/_/g, ' ');
  }
}

function CoveredRegistrationDetail({
  cr,
  parentProvider,
}: {
  cr: CoveredRegistration;
  /** Provider of the payment row this registration sits under — drives the
   *  badge on the non-credit tender leg (Stripe card/bank vs HighCall
   *  cash/check) when the entry was settled with a credit split. */
  parentProvider: Provider;
}) {
  // Show the entry/transaction-fee breakdown the same way the Funds tab
  // does so the user can reconcile this row against their entry fee
  // structure. When the backend couldn't resolve fees (legacy data),
  // skip the breakdown rather than render zeroes.
  const hasFeeBreakdown = cr.entry_fee != null || cr.transaction_fee != null;
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium truncate">{cr.event_name || 'Entry'}</p>
          <p className="text-xs text-muted-foreground truncate">
            {[cr.entrant_name, cr.rodeo_name, cr.organization_name]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <p className="flex-shrink-0 text-sm font-semibold tabular-nums">
          {formatCurrency(cr.amount)}
        </p>
      </div>
      {hasFeeBreakdown && (
        <div className="mt-2 space-y-0.5 text-xs">
          {cr.entry_fee != null && (
            <div className="flex justify-between text-muted-foreground">
              <span>Entry fee</span>
              <span className="tabular-nums">{formatCurrency(cr.entry_fee)}</span>
            </div>
          )}
          {cr.transaction_fee != null && cr.transaction_fee > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Transaction fee (non-refundable)</span>
              <span className="tabular-nums">
                {formatCurrency(cr.transaction_fee)}
              </span>
            </div>
          )}
          {cr.non_member_fee > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Non-member surcharge</span>
              <span className="tabular-nums">
                {formatCurrency(cr.non_member_fee)}
              </span>
            </div>
          )}
        </div>
      )}
      {/* Split tender — how this entry was actually settled, broken into one
          provider-badged line per tender leg so the credit reads as part of
          THIS payment (e.g. "Stripe $130 + HighCall credit $25") rather than a
          mysterious standalone line. */}
      {cr.credit_applied > 0 && (
        <div className="mt-2 pt-2 border-t border-dashed space-y-1.5 text-xs">
          <div className="font-medium text-foreground/80">Paid with</div>
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <ProviderBadge provider={parentProvider} />
              {parentProvider === 'stripe' ? 'Card / bank' : 'Cash / check'}
            </span>
            <span className="tabular-nums font-medium">
              {formatCurrency(Math.max(0, cr.amount - cr.credit_applied))}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <ProviderBadge provider="highcall" />
              Credit applied
            </span>
            <span className="tabular-nums font-medium text-emerald-700 dark:text-emerald-300">
              {formatCurrency(cr.credit_applied)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function TransactionTableRow({
  row,
  expanded,
  onToggle,
  showStripeLink,
  onDeleteAdjustment,
}: {
  row: TransactionItem;
  expanded: boolean;
  onToggle: () => void;
  /** Admin-only: render the Stripe reference as a clickable dashboard link
   *  instead of plain text. Contestants see the same reference, just not
   *  linked. */
  showStripeLink?: boolean;
  /** Admin-only: when provided, manual ledger (adjustment) rows get a trash
   *  affordance that calls this with the row. Settled entries render the
   *  button disabled (the backend rejects deleting them). Omitted for the
   *  contestant's own view. */
  onDeleteAdjustment?: (row: TransactionItem) => void;
}) {
  // A pending row mid-ACH-verification gets a distinct indigo treatment (not
  // the amber "due now" pending look) so the user reads it as in-progress, not
  // an unpaid balance to chase. Amount stays neutral — it isn't money owed-now
  // nor money in, it's a bank payment being verified.
  const style =
    row.status === 'verifying'
      ? {
          badge:
            'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
          amount: 'text-indigo-700 dark:text-indigo-300',
        }
      : row.status === 'rodeo_cancelled'
        ? {
            // Rose badge to match the "Rodeo cancelled" treatment elsewhere,
            // muted amount because the figure is informational only — it is
            // excluded from every total above.
            badge:
              'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
            amount: 'text-muted-foreground line-through',
          }
        : getKindStyle(row.kind);
  const signed = row.amount;
  const sign = signed > 0 ? '+' : signed < 0 ? '−' : '';
  const formatted = formatCurrency(Math.abs(signed));
  const isExpandable = row.covered_registrations.length > 0;

  const selectedMethod = describeSelectedMethod(row.selected_payment_method);
  // Partner-pending team-roping primary. Backend stamps the row with
  // type_label='Awaiting partner' and clears selected_payment_method so we
  // don't imply a Stripe authorization that doesn't exist; this flag drives
  // the explanatory sub-line + amber styling on the row itself.
  const isAwaitingPartner =
    row.kind === 'pending' &&
    (row.partner_status === 'needs_partner' ||
      row.partner_status === 'pending');
  const showStatusPill =
    !!row.status &&
    row.kind !== 'pending' &&
    // 'free' already says everything the pill would ("Free" + $0.00).
    row.kind !== 'free' &&
    row.kind !== 'failed' &&
    row.status.toLowerCase() !== 'completed' &&
    row.status.toLowerCase() !== 'recorded';

  // Visual highlight for rows where a buddy primary has claimed
  // responsibility for the pending obligation. The teammate still owes
  // it, but a different person has said they'll cover it — the green
  // tint + Claimed badge make it stand out in the ledger so the user
  // doesn't think they're on the hook alone.
  const isClaimed = !!row.claimed_payer_display_name;

  // Inline summary line under the Type pill, shown when the row covers
  // exactly one registration (single-row payment, pending entry, single-
  // registration cash). Bulk payments deliberately skip this — the
  // "Bulk · N" badge conveys multiplicity, and the expand reveals the
  // per-registration detail. Lets a user identify "what did this $185
  // pay for?" without expanding.
  const singleSummary =
    !row.is_bulk && row.covered_registrations.length === 1
      ? row.covered_registrations[0]
      : null;
  const singleSummaryLine = singleSummary
    ? [
        singleSummary.event_name || 'Entry',
        singleSummary.rodeo_name ? `at ${singleSummary.rodeo_name}` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null;

  // Split-tender summary for the COLLAPSED row: when a payment was settled
  // partly with org account credit, surface "card $X + credit $Y · $Z total"
  // inline so the user sees the full picture without expanding. The row's own
  // amount is just the card/cash leg (the credit-tender row is hidden); these
  // sums reconstruct the whole entry from the covered registrations.
  const creditTender = row.covered_registrations.reduce(
    (sum, cr) => sum + (cr.credit_applied || 0),
    0,
  );
  const tenderSplit =
    row.kind === 'payment' && creditTender > 0
      ? {
          credit: creditTender,
          total: row.covered_registrations.reduce(
            (sum, cr) => sum + cr.amount,
            0,
          ),
        }
      : null;

  return (
    <Fragment>
      <tr
        className={`border-b transition-colors ${
          isExpandable ? 'cursor-pointer hover:bg-muted/30' : ''
        } ${
          isClaimed
            ? 'bg-emerald-500/5 hover:bg-emerald-500/10'
            : expanded
              ? 'bg-muted/20'
              : ''
        }`}
        onClick={() => isExpandable && onToggle()}
      >
        {/* Expand chevron */}
        <td className="w-8 py-3 pl-3 align-top">
          {isExpandable && (
            <span className="inline-flex h-5 w-5 items-center justify-center text-muted-foreground">
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          )}
        </td>

        {/* Date */}
        <td className="py-3 pr-3 align-top text-xs text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.occurred_at)}
        </td>

        {/* Type + sub-line context (description / claim attribution) */}
        <td className="py-3 pr-3 align-top">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge
                variant="outline"
                className={`text-xs font-normal ${style.badge}`}
              >
                {row.type_label}
              </Badge>
              {row.is_bulk && (
                <Badge variant="outline" className="text-xs font-normal">
                  Bulk · {row.covered_registrations.length}
                </Badge>
              )}
              {/* Blocking charge (fine, etc.) that's still unpaid — flags that
                  this entry gates new event entries for its org. */}
              {row.blocks_event_entry && !row.settled_at && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
                >
                  Blocks entry
                </Badge>
              )}
              {selectedMethod && (
                <span className="text-xs text-muted-foreground">
                  · {selectedMethod}
                </span>
              )}
              {isClaimed && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                >
                  Claimed
                </Badge>
              )}
              {showStatusPill && (
                <Badge
                  variant="outline"
                  className="text-xs font-normal capitalize"
                >
                  {row.status}
                </Badge>
              )}
            </div>
            {/* Single-row summary — "Event at Rodeo" — shown on any row
                that covers exactly one registration so the user can scan
                the ledger and see what each line is for. Bulk rows use
                the badge + expand instead. Falls back to row.reason for
                rows without covered_registrations (refunds, adjustments). */}
            {singleSummaryLine ? (
              <p className="text-xs text-foreground/80">{singleSummaryLine}</p>
            ) : (
              row.reason && (
                <p className="text-xs text-foreground/80 line-clamp-2">
                  {row.reason}
                </p>
              )
            )}
            {/* Split tender at a glance — card/cash leg + credit leg + total,
                so the user sees the full settlement without expanding. */}
            {tenderSplit && (
              <p className="text-xs text-muted-foreground">
                {formatCurrency(
                  Math.max(0, tenderSplit.total - tenderSplit.credit),
                )}{' '}
                {row.provider === 'stripe' ? 'card' : 'cash/check'}
                {' + '}
                <span className="text-emerald-700 dark:text-emerald-300">
                  {formatCurrency(tenderSplit.credit)} credit
                </span>
                {' · '}
                <span className="font-medium text-foreground/80">
                  {formatCurrency(tenderSplit.total)} total
                </span>
              </p>
            )}
            {isAwaitingPartner && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Not yet charged —{' '}
                {row.partner_status === 'needs_partner'
                  ? 'partner declined; invite a new partner or cancel this entry.'
                  : 'waiting on partner approval.'}
              </p>
            )}
            {row.status === 'rodeo_cancelled' && (
              <p className="text-xs text-rose-700 dark:text-rose-300">
                This rodeo was cancelled — you owe nothing for this entry.
              </p>
            )}
            {row.claimed_payer_display_name ? (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                Claimed by {row.claimed_payer_display_name}
                {row.created_by_display_name &&
                row.created_by_display_name !== row.claimed_payer_display_name
                  ? ` · Created by ${row.created_by_display_name}`
                  : ''}
              </p>
            ) : row.created_by_display_name ? (
              <p className="text-xs text-muted-foreground">
                Created by {row.created_by_display_name}
              </p>
            ) : null}
          </div>
        </td>

        {/* Provider */}
        <td className="py-3 pr-3 align-top">
          <ProviderBadge provider={row.provider} />
        </td>

        {/* Method (Stripe payment method on captured rows) */}
        <td className="py-3 pr-3 align-top">
          <PaymentMethodSummary pm={row.payment_method} />
        </td>

        {/* Reference — for admins, a Stripe id links out to the Stripe
            dashboard; for contestants (and non-Stripe ids) it's plain text. */}
        <td className="py-3 pr-3 align-top">
          {row.reference &&
            (() => {
              const url = showStripeLink
                ? buildStripeDashboardUrl(row.reference)
                : null;
              if (url) {
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:underline break-all"
                    title="View in Stripe dashboard"
                  >
                    {row.reference}
                    <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                  </a>
                );
              }
              return (
                <span className="font-mono text-[10px] text-muted-foreground break-all">
                  {row.reference}
                </span>
              );
            })()}
        </td>

        {/* Amount (+ admin delete affordance for manual ledger rows) */}
        <td className="py-3 pr-3 align-top text-right">
          <div className="inline-flex items-center justify-end gap-1.5">
            <span
              className={`text-sm font-semibold tabular-nums ${style.amount}`}
            >
              {sign}
              {formatted}
            </span>
            {onDeleteAdjustment && row.kind === 'adjustment' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!row.settled_at) onDeleteAdjustment(row);
                }}
                disabled={!!row.settled_at}
                className="p-1 rounded-md text-muted-foreground transition-colors hover:text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                title={
                  row.settled_at
                    ? 'Settled by a Stripe payment — refund the payment to reverse it'
                    : 'Delete this manual ledger entry'
                }
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span className="sr-only">Delete entry</span>
              </button>
            )}
          </div>
        </td>
      </tr>

      {expanded && isExpandable && (
        <tr className="border-b bg-muted/10">
          <td colSpan={7} className="p-4">
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {row.is_bulk
                  ? 'Covered registrations'
                  : row.kind === 'pending' || row.kind === 'free'
                    ? 'Registration detail'
                    : 'Registration'}
              </p>
              <div className="space-y-2">
                {row.covered_registrations.map((cr) => (
                  <CoveredRegistrationDetail
                    key={cr.registration_id}
                    cr={cr}
                    parentProvider={row.provider}
                  />
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function TransactionHistoryTab({
  userId,
  showStripeDashboardLinks = false,
  onDeleteAdjustment,
}: {
  /** Admin override: render another contestant's feed (admin User-Details →
   *  Financials). Omit for the signed-in user's own Profile → Financials. */
  userId?: string;
  /** Admin-only: link Stripe references out to the Stripe dashboard. */
  showStripeDashboardLinks?: boolean;
  /** Admin-only: enables the delete affordance on manual ledger rows. The
   *  admin tab owns the confirm dialog + mutation. */
  onDeleteAdjustment?: (row: TransactionItem) => void;
} = {}) {
  const isAdminView = !!userId;
  const [includeFailed, setIncludeFailed] = useState(false);
  const { data, isLoading, error } = useTransactionHistory({
    includeFailed,
    userId,
  });

  const [filterChips, setFilterChips] = useState<Set<FilterChip>>(
    () => new Set<FilterChip>(['all']),
  );
  const toggleChip = (chip: FilterChip) => {
    setFilterChips((prev) => {
      if (chip === 'all') return new Set<FilterChip>(['all']);
      const next = new Set(prev);
      if (next.has('all')) {
        return new Set<FilterChip>([chip]);
      }
      if (next.has(chip)) {
        next.delete(chip);
        if (next.size === 0) return new Set<FilterChip>(['all']);
      } else {
        next.add(chip);
      }
      return next;
    });
  };

  // Org filter — multi-select, "All orgs" by default. Same exclusive-'all'
  // chip behaviour as the type filter above. Only rendered when the feed spans
  // more than one org (no point showing a single-option filter).
  const [orgFilter, setOrgFilter] = useState<Set<string>>(
    () => new Set<string>([ALL_ORGS]),
  );
  const toggleOrg = (org: string) => {
    setOrgFilter((prev) => {
      if (org === ALL_ORGS) return new Set<string>([ALL_ORGS]);
      const next = new Set(prev);
      if (next.has(ALL_ORGS)) return new Set<string>([org]);
      if (next.has(org)) {
        next.delete(org);
        if (next.size === 0) return new Set<string>([ALL_ORGS]);
      } else {
        next.add(org);
      }
      return next;
    });
  };

  // Track expanded rows by id so users can keep multiple open while
  // comparing. Same pattern the Funds tab uses.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Date-range filter — narrows the visible rows (the summary buckets stay
  // account-wide, like the Type/Org filters).
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const dateRange = useMemo(
    () => computeDateRange(datePreset, customStart, customEnd),
    [datePreset, customStart, customEnd],
  );

  const items = data?.items ?? [];
  // Standalone credit-tender rows are NOT shown as their own line — the credit
  // is surfaced inside the event payment's expansion ("$X credit + $Y card").
  // They still count toward the totals below (Paid + the ledger-net math).
  const isCreditTender = (row: TransactionItem) =>
    row.kind === 'payment' && row.type_label === 'Credit applied';
  const visible = useMemo(
    () =>
      items.filter(
        (row) =>
          !isCreditTender(row) &&
          matchesFilter(row, filterChips) &&
          matchesOrg(row, orgFilter) &&
          inDateRange(row, dateRange),
      ),
    [items, filterChips, orgFilter, dateRange],
  );

  // Distinct orgs across the displayable feed, for the org-filter chips. Built
  // from the same rows the table can show (credit-tender rows are hidden, so
  // they don't contribute an org). NO_ORG sorts last under an "Other" label.
  const orgOptions = useMemo(() => {
    const names = new Set<string>();
    let hasNoOrg = false;
    for (const row of items) {
      if (isCreditTender(row)) continue;
      for (const n of rowOrgNames(row)) {
        if (n === NO_ORG) hasNoOrg = true;
        else names.add(n);
      }
    }
    const sorted = [...names].sort((a, b) => a.localeCompare(b));
    return hasNoOrg ? [...sorted, NO_ORG] : sorted;
  }, [items]);

  // Bucket totals — same shape as a bank statement summary.
  const totals = useMemo(() => {
    let payments = 0;
    let refunds = 0;
    let manualNet = 0;
    let pendingTotal = 0;
    let claimedByOthers = 0;
    // Credit redeemed at checkout (the credit-tender payments). Counted in
    // Paid like any tender, and added back into the ledger net so the credit
    // grant on the ledger shows its REMAINING value (grant − redeemed).
    let redeemed = 0;
    for (const r of items) {
      if (r.kind === 'payment') {
        payments += r.amount;
        if (isCreditTender(r)) redeemed += r.amount;
      } else if (r.kind === 'refund') refunds += r.amount;
      else if (r.kind === 'adjustment') {
        // Only UNSETTLED ledger entries count toward the running net. Once a
        // manual charge is paid off (settled_at set), it's already reflected
        // in Paid / Total charges — counting it here too would leave a
        // phantom ledger balance after the contestant clears their charges.
        // A still-open credit keeps contributing a negative (credit on file).
        if (!r.settled_at) manualNet += r.amount;
      } else if (r.kind === 'pending') {
        // The producer cancelled the rodeo: nothing will ever be collected, so
        // this must not feed Still owed (or Total charges, which is defined as
        // paid + still owed — counting it there would break the
        // "Total charges − Paid = Still owed" identity the caption states).
        // The row stays visible in the feed, badged "Rodeo cancelled".
        if (r.status !== 'rodeo_cancelled') {
          pendingTotal += r.amount;
          if (r.claimed_payer_display_name) claimedByOthers += r.amount;
        }
      }
    }
    // Total charges = everything the account has been billed for the entry
    // story = what's been paid + what's still owed. Surfaced so the user can
    // reconcile the row at a glance: Total charges − Paid = Still owed. Manual
    // ledger activity keeps its own bucket (settled-vs-unsettled accounting is
    // ambiguous to fold in here without risking a double-count), so this stays
    // the registration-fee picture.
    const totalCharges = payments + pendingTotal;
    // Fold redemptions into the ledger net: the credit grant sits unsettled at
    // its full amount, so adding back the redeemed (positive) amounts yields
    // the REMAINING credit (e.g. −$75 grant + $50 redeemed = −$25 left).
    const ledgerNet = manualNet + redeemed;
    return {
      payments,
      refunds,
      manualNet: ledgerNet,
      pendingTotal,
      claimedByOthers,
      totalCharges,
    };
  }, [items]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Receipt className="h-5 w-5" />
            Transaction history
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Every payment, refund, and ledger entry tied to{' '}
            {isAdminView ? 'this account' : 'your account'}. Click a row to see
            which registration(s) it covered.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {!isLoading && items.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <SummaryStat
                  label="Total charges"
                  amount={totals.totalCharges}
                  color="text-foreground"
                  footnote="Paid + still owed"
                />
                <SummaryStat
                  label="Paid"
                  amount={totals.payments}
                  color="text-emerald-700 dark:text-emerald-300"
                  // Paid money reduces what's owed, so show it as −$X
                  // (still green) against the Total charges.
                  forceSign="−"
                />
                <SummaryStat
                  label="Refunds"
                  amount={totals.refunds}
                  color="text-violet-700 dark:text-violet-300"
                />
                <SummaryStat
                  label="Ledger (net)"
                  amount={totals.manualNet}
                  color="text-blue-700 dark:text-blue-300"
                />
                <SummaryStat
                  label="Still owed"
                  amount={totals.pendingTotal}
                  color="text-amber-700 dark:text-amber-300"
                  // When a buddy primary has claimed responsibility for any
                  // pending rows, surface the split so the entrant sees
                  // "$X claimed by others" without losing the overall
                  // obligation amount.
                  footnote={
                    totals.claimedByOthers > 0
                      ? `incl. ${formatCurrency(
                          totals.claimedByOthers,
                        )} claimed by others`
                      : null
                  }
                />
              </div>
              {/* Explicit reconciliation line so the relationship between the
                  buckets is obvious at a glance. */}
              <p className="text-xs text-muted-foreground">
                Total charges {formatCurrency(totals.totalCharges)} − Paid{' '}
                {formatCurrency(totals.payments)} ={' '}
                <span className="font-medium text-amber-700 dark:text-amber-300">
                  {formatCurrency(totals.pendingTotal)} still owed
                </span>
                .
              </p>
            </>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Show
            </span>
            <FilterChipButton
              label="All"
              active={filterChips.has('all')}
              onClick={() => toggleChip('all')}
            />
            <FilterChipButton
              label="Payments"
              active={filterChips.has('payments') && !filterChips.has('all')}
              onClick={() => toggleChip('payments')}
            />
            <FilterChipButton
              label="Refunds"
              active={filterChips.has('refunds') && !filterChips.has('all')}
              onClick={() => toggleChip('refunds')}
            />
            <FilterChipButton
              label="Manual"
              active={filterChips.has('manual') && !filterChips.has('all')}
              onClick={() => toggleChip('manual')}
            />
            <FilterChipButton
              label="Pending"
              active={filterChips.has('pending') && !filterChips.has('all')}
              onClick={() => toggleChip('pending')}
            />
            <FilterChipButton
              label="Free"
              active={filterChips.has('free') && !filterChips.has('all')}
              onClick={() => toggleChip('free')}
            />
            <div className="ml-auto flex items-center gap-2">
              <Label
                htmlFor="failed-toggle"
                className="text-xs text-muted-foreground cursor-pointer"
              >
                Show failed attempts
              </Label>
              <Switch
                id="failed-toggle"
                checked={includeFailed}
                onCheckedChange={setIncludeFailed}
              />
            </div>
          </div>

          {/* Org filter — only when the feed spans more than one org. */}
          {orgOptions.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Org
              </span>
              <FilterChipButton
                label="All orgs"
                active={orgFilter.has(ALL_ORGS)}
                onClick={() => toggleOrg(ALL_ORGS)}
              />
              {orgOptions.map((org) => (
                <FilterChipButton
                  key={org}
                  label={org === NO_ORG ? 'Other' : org}
                  active={orgFilter.has(org) && !orgFilter.has(ALL_ORGS)}
                  onClick={() => toggleOrg(org)}
                />
              ))}
            </div>
          )}

          {/* Date-range filter — preset windows + a custom span. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Date
            </span>
            {DATE_PRESETS.map((p) => (
              <FilterChipButton
                key={p.key}
                label={p.label}
                active={datePreset === p.key}
                onClick={() => setDatePreset(p.key)}
              />
            ))}
            {datePreset === 'custom' && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="h-7 rounded-md border bg-background px-2 text-xs"
                  aria-label="Start date"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="h-7 rounded-md border bg-background px-2 text-xs"
                  aria-label="End date"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-8">
            <EmptyState
              icon={CircleAlert}
              title="Couldn't load transactions"
              description={
                (error as any)?.message ||
                'Please try again in a moment, or contact support if this keeps happening.'
              }
            />
          </CardContent>
        </Card>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <EmptyState
              icon={Receipt}
              title={
                items.length === 0
                  ? 'No transactions yet'
                  : 'No transactions match this filter'
              }
              description={
                items.length === 0
                  ? "When you pay an entry fee or an admin records a charge, it'll show up here."
                  : 'Try selecting a different filter above to see more.'
              }
            />
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-8" />
                  <th className="text-left font-medium px-3 py-2">Date</th>
                  <th className="text-left font-medium px-3 py-2">Type</th>
                  <th className="text-left font-medium px-3 py-2">Provider</th>
                  <th className="text-left font-medium px-3 py-2">Method</th>
                  <th className="text-left font-medium px-3 py-2">Reference</th>
                  <th className="text-right font-medium px-3 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <TransactionTableRow
                    key={row.id}
                    row={row}
                    expanded={expandedIds.has(row.id)}
                    onToggle={() => toggleExpanded(row.id)}
                    showStripeLink={showStripeDashboardLinks}
                    onDeleteAdjustment={onDeleteAdjustment}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function SummaryStat({
  label,
  amount,
  color,
  footnote,
  // Force the leading sign regardless of the amount's own sign. Used for the
  // "Paid" bucket: the value is stored positive (money in) but it reduces the
  // total owed, so it reads as a negative (−$155) against the charges — while
  // keeping its green "money handled" color. Null on a zero amount so we don't
  // render a lonely "−$0.00".
  forceSign,
}: {
  label: string;
  amount: number;
  color: string;
  footnote?: string | null;
  forceSign?: '+' | '−';
}) {
  const sign =
    forceSign && amount !== 0
      ? forceSign
      : amount > 0
        ? '+'
        : amount < 0
          ? '−'
          : '';
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`mt-0.5 text-base font-semibold tabular-nums ${color}`}>
        {sign}
        {formatCurrency(Math.abs(amount))}
      </p>
      {footnote && (
        <p className="mt-0.5 text-[10px] text-muted-foreground">{footnote}</p>
      )}
    </div>
  );
}

function FilterChipButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant={active ? 'default' : 'outline'}
      size="sm"
      onClick={onClick}
      className="h-7 rounded-full px-3 text-xs"
    >
      {label}
    </Button>
  );
}
